import { Entity } from "ecsy";
import * as particles from 'pixi-particles';
import * as PIXI from 'pixi.js';
import { claimVec2 } from "../../../shared/box2dUtils";
import { Dead, Physics } from "../../../shared/components";
import { Resolution } from "../../../shared/constants";
import { getEntityById } from "../../../shared/entityFactory";
import { angleFromUp } from "../../../shared/utils";
import { loadParticleAssets } from "./assetLoader";

export class ParticleFactory {
	constructor(public particleContainer: particles.LinkedListContainer) { }

	particleAssets: { [key: string]: PIXI.Texture } = {};

	async loadAssets() {
		this.particleAssets = await loadParticleAssets();
	}

	loadTextureFromPack(particlePack: Map<string, Uint8Array>, filename: string): PIXI.Texture {
		const u8 = particlePack.get(filename);
		if (!u8) {
			throw new Error(`ParticleFactory: loadTextureFromPack: could not find ${filename} in particle pack`);
		}
		const blob = new Blob([u8.slice()], { type: 'image/png' });
		return PIXI.Texture.from(URL.createObjectURL(blob));
	}

	Spatter(entity: Entity) {
		return new particles.Emitter(
			this.particleContainer,
			[this.particleAssets['particle']],
			{
				"alpha": {
					"start": 0.8,
					"end": 0.1
				},
				"scale": {
					"start": 1,
					"end": 3,
					"minimumScaleMultiplier": 1
				},
				"color": {
					"start": "#fb1010",
					"end": "#f20000"
				},
				"speed": {
					"start": 0,
					"end": 0,
					"minimumSpeedMultiplier": 1
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"maxSpeed": 0,
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"noRotation": false,
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.01,
					"max": 0.2
				},
				"blendMode": "normal",
				"frequency": 0.001,
				"emitterLifetime": 0.1,
				"maxParticles": 40,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "rect",
				"spawnRect": {
					"x": 0,
					"y": 0,
					"w": Resolution.Width,
					"h": Resolution.Height
				}
			}
		);
	}

	HealBurst(entity: Entity) {
		return new particles.Emitter(
			this.particleContainer,
			[
				this.particleAssets['healParticle'],
			],
			{
				"alpha": {
					"start": 1,
					"end": 0.43
				},
				"scale": {
					"start": 0.3,
					"end": 1,
					"minimumScaleMultiplier": 1
				},
				"color": {
					"start": "#28f050",
					"end": "#7a746a"
				},
				"speed": {
					"start": 100,
					"end": 100,
					"minimumSpeedMultiplier": 1
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"maxSpeed": 0,
				"startRotation": {
					"min": 270,
					"max": 270
				},
				"noRotation": false,
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.1,
					"max": 0.6
				},
				"blendMode": "normal",
				"frequency": 0.01,
				"emitterLifetime": 0.1,
				"maxParticles": 200,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "rect",
				"spawnRect": {
					"x": 0,
					"y": 0,
					"w": 20,
					"h": 20
				}
			}
		)
	}

	CellBurst(entity: Entity, maxLifetime = 1.8, asset = 'particle', container: PIXI.Container = this.particleContainer) {
		const dead = entity.getComponent(Dead);
		let minAngle = 0;
		let maxAngle = 360;

		//Compute the direction of spread from the velocities of the killer and the dead, if possible
		do {
			if (!dead) break;
			if (dead.killerId === null) break;
			const killer = getEntityById(dead.killerId);
			if (!killer) break;
			const killerPhysics = killer.getComponent(Physics);
			if (!killerPhysics) break;

			//Set start and end angle so particles go in direction of killer, ie opposite direction as impact
			const exitDirection = claimVec2(killerPhysics.body.GetLinearVelocity());
			if (exitDirection.length() < 2 && entity.hasComponent(Physics)) {
				const ourVelocity = claimVec2(entity.getComponent(Physics).body.GetLinearVelocity());
				ourVelocity.negate().copy(exitDirection);
			}
			exitDirection.normalize();
			const degrees = angleFromUp(exitDirection) * -PIXI.RAD_TO_DEG - 45;
			minAngle = degrees - 45;
			maxAngle = degrees + 45;
		} while (false)

		return new particles.Emitter(
			container,
			[this.particleAssets[asset]],
			{
				"alpha": {
					"start": 1,
					"end": 0.1
				},
				"scale": {
					"start": 0.15,
					"end": 0.15,
				},
				"color": {
					"start": "#ff00ff",
					"end": "#b1e5fa"
				},
				"speed": {
					"start": 300,
					"end": 100
				},
				"startRotation": {
					"min": minAngle,
					"max": maxAngle
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.5,
					"max": maxLifetime
				},
				"frequency": 0.003,
				"emitterLifetime": .1,
				"maxParticles": 175,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "circle",
				"spawnCircle": {
					"x": 0,
					"y": 0,
					"r": 0
				}
			}
		)
	}

	Explosion(startRadius: number, endRadius: number, duration: number, startColor = "fb1010", endColor = "f5b830") {
		const speed = (endRadius - startRadius) / duration;
		return new particles.Emitter(
			this.particleContainer,
			[this.particleAssets['particle']],
			{
				"alpha": {
					"start": 0.8,
					"end": 0.6
				},
				"scale": {
					"start": 0.5,
					"end": 1
				},
				"color": {
					"start": startColor,
					"end": endColor
				},
				"speed": {
					"start": speed,
					"end": speed,
				},
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": duration - 0.2,
					"max": duration,
				},
				"frequency": 0.001,
				"emitterLifetime": 0.1,
				"maxParticles": 1000,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "circle",

				"spawnCircle": {
					"x": 0,
					"y": 0,
					"r": startRadius
				}
			}
		);
	}

	Wavefront(
		startRadius: number,
		endRadius: number,
		duration: number,
		startColor = "e3f9ff",
		endColor = "0ec8f8",
		startAlpha = 0.8,
		endAlpha = 0.7,
		scale = 1,
		container: PIXI.Container = this.particleContainer
	) {
		const speed = (endRadius - startRadius) / duration;
		return new particles.Emitter(
			container,
			[this.particleAssets['particle']],
			{
				"alpha": {
					"start": startAlpha,
					"end": endAlpha
				},
				"scale": {
					"start": scale,
					"end": scale * .3,
				},
				"color": {
					"start": startColor,
					"end": endColor
				},
				"speed": {
					"start": speed,
					"end": speed,
					"minimumSpeedMultiplier": 1
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"maxSpeed": 0,
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"noRotation": false,
				"rotationSpeed": {
					"min": 0,
					"max": 200
				},
				"lifetime": {
					"min": duration,
					"max": duration
				},
				"blendMode": "normal",
				"frequency": 0.008,
				"emitterLifetime": 0.1,
				"maxParticles": 100,
				"pos": {
					"x": 0,
					"y": 0
				},
				"spawnType": "burst",
				"particlesPerWave": 100,
				"particleSpacing": 4
			}
		);
	}

	WaveCollapse(
		startRadius: number,
		endRadius: number,
		duration: number,
		startColor = "e3f9ff",
		endColor = "0ec8f8",
		startAlpha = 0.8,
		endAlpha = 0.7,
		scale = 1,
		container: PIXI.Container = this.particleContainer
	) {
		const speed = (startRadius - endRadius) / duration;
		return new particles.Emitter(
			container,
			[this.particleAssets['particle']],
			{
				"alpha": {
					"start": startAlpha,
					"end": endAlpha
				},
				"scale": {
					"start": scale * .5,
					"end": scale,
				},
				"color": {
					"start": startColor,
					"end": endColor
				},
				"speed": {
					"start": -speed,
					"end": -speed,
					"minimumSpeedMultiplier": 1
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"startRotation": {
					"min": 0,
					"max": 0
				},
				"noRotation": true,
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": duration,
					"max": duration
				},
				"blendMode": "normal",
				"frequency": 0.008,
				"emitterLifetime": 0.1,
				"maxParticles": 400,
				"pos": {
					"x": 0,
					"y": 0
				},
				"spawnType": "ring",
				"spawnCircle": { x: 0, y: 0, r: startRadius, minR: startRadius },
				"particlesPerWave": 100,
				"particleSpacing": 4
			}
		);
	}

	VirusDeathIndicator(
		startRadius: number,
		endRadius: number,
		duration: number,
		startAlpha = 1.0,
		endAlpha = 1.0,
		startColor = "ffffff",
		endColor = "ffffff",
		scale = 1,
		container: PIXI.Container = this.particleContainer
	) {
		const speed = 0; //(endRadius - startRadius) / duration;
		return new particles.Emitter(
			container,
			[this.particleAssets['particle']],
			{
				"alpha": {
					"start": startAlpha,
					"end": endAlpha
				},
				"scale": {
					"start": startRadius,
					"end": endRadius,
				},
				"color": {
					"start": startColor,
					"end": endColor
				},
				"speed": {
					"start": speed,
					"end": speed,
					"minimumSpeedMultiplier": 1
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"maxSpeed": 0,
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"noRotation": false,
				"rotationSpeed": {
					"min": 0,
					"max": 200
				},
				"lifetime": {
					"min": duration,
					"max": duration
				},
				"blendMode": "normal",
				"frequency": duration / 2,
				"emitterLifetime": duration,
				"maxParticles": 100,
				"pos": {
					"x": 0,
					"y": 0
				},
				"spawnType": "burst",
				"particlesPerWave": 20,
				"particleSpacing": 4
			}
		);
	}

	TagVirus(startRadius: number, endRadius: number, duration: number) {
		const radius = endRadius;
		const speed = 400;
		return new particles.Emitter(
			this.particleContainer,
			[this.particleAssets['particle']],
			{
				"alpha": {
					"start": 1.0,
					"end": 0.5
				},
				"scale": {
					"start": .4,
					"end": .03
				},
				"color": {
					"start": "f8a6ac",
					"end": "f8a6ac",
				},
				"speed": {
					"start": speed,
					"end": speed / 2,
					"minimumSpeedMultiplier": 1
				},
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": radius / speed / 2,
					"max": radius / speed
				},
				"frequency": 0.001,
				"emitterLifetime": 0.1,
				"maxParticles": 10,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "point",
			}
		);
	}

	HealWave(startRadius: number, endRadius: number, duration: number) {
		return new particles.Emitter(
			this.particleContainer,
			[this.particleAssets['kennyParticle']],
			{
				"alpha": {
					"start": 1,
					"end": .2
				},
				"scale": {
					"start": 0.05,
					"end": 0.1
				},
				"color": {
					"start": "2bc249",
					"end": "26ff00"
				},
				"speed": {
					"start": 0,
					"end": 0,
				},
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.5,
					"max": 1
				},
				"frequency": 0.001,
				"emitterLifetime": .1,
				"maxParticles": 65,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "circle",
				"spawnCircle": {
					"x": 0,
					"y": 0,
					"r": endRadius
				}
			}
		);
	}

	SellCoins() {
		return new particles.Emitter(
			this.particleContainer,
			[this.particleAssets['coin']],
			{
				"alpha": {
					"start": 0.09,
					"end": 1
				},
				"scale": {
					"start": 1,
					"end": 1,
					"minimumScaleMultiplier": 1
				},
				"color": {
					"start": "#ffffff",
					"end": "#ffffff"
				},
				"speed": {
					"start": 200,
					"end": 100,
					"minimumSpeedMultiplier": 1
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"maxSpeed": 0,
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"noRotation": false,
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.2,
					"max": 0.5
				},
				"blendMode": "normal",
				"frequency": 0.01,
				"emitterLifetime": 0.3,
				"maxParticles": 10,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "point"
			}
		);
	}

	Bubbles(radius: number) {
		return new particles.Emitter(
			this.particleContainer,
			[this.particleAssets['bubbleParticle']],
			{
				"alpha": {
					"start": 1,
					"end": 1
				},
				"scale": {
					"start": 0.08,
					"end": 0.15
				},
				"color": {
					"start": "ffffff",
					"end": "ffffff"
				},
				"speed": {
					"start": 300,
					"end": 300,
					"minimumSpeedMultiplier": 0.7
				},
				"startRotation": {
					"min": 0,
					"max": 180
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.2,
					"max": 0.3
				},
				"frequency": 0.001,
				"emitterLifetime": .1,
				"maxParticles": 50,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "circle",
				"spawnCircle": {
					"x": 0,
					"y": 0,
					"r": radius
				}
			}
		);
	}

	Alert(container: PIXI.Container = this.particleContainer) {
		return new particles.Emitter(
			container,
			[this.particleAssets['exclamationMark']],
			{
				"alpha": {
					"start": 1,
					"end": 1
				},
				"scale": {
					"start": 0.4,
					"end": 0.5
				},
				// "color": {
				// 	"start": "ffffff",
				// 	"end": "ffffff"
				// },
				"speed": {
					"start": 0,
					"end": 0,
					"minimumSpeedMultiplier": 0.7
				},
				"startRotation": {
					"min": 0,
					"max": 0
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.6,
					"max": 0.6
				},
				"frequency": 0.001,
				"emitterLifetime": .1,
				"maxParticles": 1,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "point",
			}
		);
	}

	Question(container: PIXI.Container = this.particleContainer) {
		return new particles.Emitter(
			container,
			[this.particleAssets['questionMark']],
			{
				"alpha": {
					"start": 1,
					"end": 1
				},
				"scale": {
					"start": 0.4,
					"end": 0.5
				},
				// "color": {
				// 	"start": "ffffff",
				// 	"end": "ffffff"
				// },
				"speed": {
					"start": 0,
					"end": 0,
					"minimumSpeedMultiplier": 0.7
				},
				"startRotation": {
					"min": 0,
					"max": 0
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.6,
					"max": 0.6
				},
				"frequency": 0.001,
				"emitterLifetime": .1,
				"maxParticles": 1,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "point",
			}
		);
	}

	Electricity() {
		const sprites = [
			this.particleAssets['electricity1'],
			this.particleAssets['electricity2'],
			this.particleAssets['electricity3'],
		];
		const animatedParticleData: particles.AnimatedParticleArt = {
			textures: sprites,
			framerate: 5,
			loop: true,
		}
		const emitter = new particles.Emitter(
			this.particleContainer,
			animatedParticleData,
			{
				"alpha": {
					"start": 1.0,
					"end": 0.5
				},
				"orderedArt": true,
				"scale": {
					"start": 0.05,
					"end": 0.1
				},
				// "color": {
				// 	"start": "ffffff",
				// 	"end": "ffffff"
				// },
				"speed": {
					"start": 40,
					"end": 40,
					"minimumSpeedMultiplier": 0.7
				},
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": 0.2,
					"max": 0.6
				},
				"frequency": 0.001,
				"emitterLifetime": 0.1,
				"maxParticles": 10,
				"pos": {
					"x": -30,
					"y": -30
				},
				"addAtBack": false,
				"spawnType": "rect",
				"spawnRect": {
					"x": 0,
					"y": 0,
					"w": 60,
					"h": 60
				}
			}
		)
		emitter.particleConstructor = particles.AnimatedParticle;
		return emitter;
	}

	LightingBolt(rotationDegrees = 0) {
		const sprites = [
			this.particleAssets['electricity2'],
		];
		const animatedParticleData: particles.AnimatedParticleArt = {
			textures: sprites,
			framerate: 5,
			loop: true,
		}
		const emitter = new particles.Emitter(
			this.particleContainer,
			sprites,
			{
				"alpha": {
					"start": 1,
					"end": 0.16
				},
				"scale": {
					"start": 0.1,
					"end": 0.2,
					"minimumScaleMultiplier": 0.2
				},
				"color": {
					"start": "ffffff",
					"end": "ffffff"
				},
				"speed": {
					"start": 300,
					"end": 300
				},
				"startRotation": {
					"min": 270 + rotationDegrees,
					"max": 270 + rotationDegrees
				},
				"rotationSpeed": {
					"min": 0,
					"max": 20
				},
				"lifetime": {
					"min": 1.8,
					"max": 2
				},
				"blendMode": "normal",
				"frequency": 0.1,
				"emitterLifetime": 0,
				"maxParticles": 200,
				"pos": {
					"x": 0,
					"y": 0
				},
				"addAtBack": false,
				"spawnType": "point",
				"extraData":
				{
					//@ts-ignore
					"path": x => -10 * Math.floor(x / 100)
				}
			})
		emitter.particleConstructor = particles.PathParticle;
		return emitter;
	}

	GrowingBall(
		startRadius: number,
		endRadius: number,
		duration: number,
		startAlpha = 1.0,
		endAlpha = 1.0,
		startColor = "ffffff",
		endColor = "ffffff",
		container: PIXI.Container = this.particleContainer
	) {
		const texture = this.particleAssets['particle'];
		const baseRadius = texture.width
		return new particles.Emitter(
			container,
			[texture],
			{
				"alpha": {
					"start": startAlpha,
					"end": endAlpha
				},
				"scale": {
					"start": startRadius / baseRadius,
					"end": endRadius / baseRadius,
				},
				"color": {
					"start": startColor,
					"end": endColor
				},
				"speed": {
					"start": 0,
					"end": 0,
					"minimumSpeedMultiplier": 0
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"maxSpeed": 0,
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"noRotation": false,
				"rotationSpeed": {
					"min": 0,
					"max": 200
				},
				"lifetime": {
					"min": duration,
					"max": duration
				},
				"blendMode": "normal",
				"frequency": duration / 2,
				"emitterLifetime": duration,
				"maxParticles": 100,
				"pos": {
					"x": 0,
					"y": 0
				},
				"spawnType": "point",
				"particlesPerWave": 1,
				"particleSpacing": 4
			}
		);
	}

	BloodSplatter(scale: number, startAlpha: number, endAlpha: number, duration: number) {
		return new particles.Emitter(
			this.particleContainer,
			[this.particleAssets['bloodSplatter']],
			{
				"alpha": {
					"start": startAlpha,
					"end": endAlpha
				},
				"scale": {
					"start": scale,
					"end": scale,
				},
				// "color": {
				// 	"start": startColor,
				// 	"end": endColor
				// },
				"speed": {
					"start": 0,
					"end": 0,
					"minimumSpeedMultiplier": 0
				},
				"acceleration": {
					"x": 0,
					"y": 0
				},
				"maxSpeed": 0,
				"startRotation": {
					"min": 0,
					"max": 360
				},
				"noRotation": false,
				"rotationSpeed": {
					"min": 0,
					"max": 0
				},
				"lifetime": {
					"min": duration * 0.7,
					"max": duration
				},
				"blendMode": "normal",
				"frequency": duration / 2,
				"emitterLifetime": duration,
				"maxParticles": 1,
				"pos": {
					"x": 0,
					"y": 0
				},
				"spawnType": "point",
				// "spawnType": "rect",
				// "spawnRect": {
				// 	"x": 0,
				// 	"y": 0,
				// 	"w": Resolution.Width,
				// 	"h": Resolution.Height
				// },
				// "particlesPerWave": 75,
				// "particleSpacing": 0
			}
		);
	}

}